/**
 * W-092 — Audit log. Stories: US-A13.
 *
 * AC2 filter by actor, action type and date range
 * AC3 append-only — this screen offers no create, edit or delete, and the
 *     access matrix denies those actions to every role including super_admin.
 *     The real guarantee is server-side: no client insert or update policy
 *     exists on `audit_log` (docs/03 §4.3).
 *
 * Super Admin only.
 */
import { List, useTable } from '@refinedev/antd'
import type { CrudFilters } from '@refinedev/core'
import { Alert, Button, DatePicker, Input, Select, Space, Table, Tag, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useState } from 'react'
import { downloadCsv } from '@/lib/csv'
import { ModelInsights } from '@/components'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { AuditLogEntry } from '@/types'

interface AuditRow extends AuditLogEntry {
  /** Joined for display via the admin view. */
  actor_email?: string | null
}

/** The vocabulary written by lib/audit.ts and by the privileged edge functions. */
const ACTION_GROUPS = [
  'subject.publish',
  'subject.unpublish',
  'subject.delete',
  'content.publish',
  'content.unpublish',
  'content.delete',
  'user.deactivate',
  'user.ban',
  'subscription.grant',
  'subscription.revoke',
  'plan.price_change',
  'notification.send',
  'cms.publish',
  'admin_user.create',
  'admin_user.role_change',
]

export const AuditLogPage = () => {
  const translate = useAdminTranslate()
  const [actor, setActor] = useState('')
  const [action, setAction] = useState<string | undefined>()
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)

  const { tableProps, setFilters } = useTable<AuditRow>({
    resource: 'audit_log',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 50 },
    syncWithLocation: true,
  })

  const apply = () => {
    const filters: CrudFilters = []
    if (actor) filters.push({ field: 'actor_email', operator: 'contains', value: actor })
    if (action) filters.push({ field: 'action', operator: 'eq', value: action })
    if (range) {
      filters.push({
        field: 'created_at',
        operator: 'gte',
        value: range[0].startOf('day').toISOString(),
      })
      filters.push({
        field: 'created_at',
        operator: 'lte',
        value: range[1].endOf('day').toISOString(),
      })
    }
    setFilters(filters, 'replace')
  }

  const rows = (tableProps.dataSource ?? []) as AuditRow[]

  return (
    <List
      title={translate('admin.pages.auditLog')}
      canCreate={false}
      headerButtons={
        <Button
          onClick={() =>
            downloadCsv(translate('admin.screens.auditLog.csvFileName'), rows, [
              {
                header: translate('admin.screens.auditLog.timestamp'),
                value: row => row.created_at,
              },
              {
                header: translate('admin.screens.auditLog.actor'),
                value: row => row.actor_email ?? row.actor_id,
              },
              { header: translate('admin.screens.auditLog.action'), value: row => row.action },
              { header: translate('admin.screens.auditLog.target'), value: row => row.target },
              { header: translate('admin.screens.auditLog.targetId'), value: row => row.target_id },
            ])
          }
        >
          {translate('admin.common.exportCsv')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="audit" />
        <Alert
          type="info"
          showIcon
          message={translate('admin.screens.auditLog.appendOnlyTitle')}
          description={translate('admin.screens.auditLog.appendOnlyBody')}
        />

        <Space wrap>
          <Input
            placeholder={translate('admin.screens.auditLog.actorPlaceholder')}
            style={{ width: 240 }}
            value={actor}
            onChange={event => setActor(event.target.value)}
          />
          <Select
            allowClear
            placeholder={translate('admin.screens.auditLog.actionType')}
            style={{ width: 240 }}
            value={action}
            onChange={value => setAction(value)}
            options={ACTION_GROUPS.map(value => ({ value, label: value }))}
          />
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            value={range}
            onChange={value =>
              setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
            }
          />
          <Button type="primary" onClick={apply}>
            {translate('admin.screens.auditLog.filter')}
          </Button>
        </Space>

        <Table<AuditRow>
          {...tableProps}
          rowKey="id"
          expandable={{
            expandedRowRender: record => (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text strong>
                  {translate('admin.screens.auditLog.before')}
                </Typography.Text>
                <pre style={{ margin: 0, overflowX: 'auto' }}>
                  {JSON.stringify(record.before ?? {}, null, 2)}
                </pre>
                <Typography.Text strong>
                  {translate('admin.screens.auditLog.after')}
                </Typography.Text>
                <pre style={{ margin: 0, overflowX: 'auto' }}>
                  {JSON.stringify(record.after ?? {}, null, 2)}
                </pre>
              </Space>
            ),
          }}
          columns={[
            {
              title: translate('admin.screens.auditLog.timestamp'),
              dataIndex: 'created_at',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
            },
            {
              title: translate('admin.screens.auditLog.actor'),
              dataIndex: 'actor_email',
              render: (value: string | null, row: AuditRow) =>
                value ?? row.actor_id ?? translate('admin.screens.auditLog.system'),
            },
            {
              title: translate('admin.screens.auditLog.action'),
              dataIndex: 'action',
              render: (value: string) => <Tag>{value}</Tag>,
            },
            { title: translate('admin.screens.auditLog.target'), dataIndex: 'target' },
            {
              title: translate('admin.columns.identifier'),
              dataIndex: 'target_id',
              render: (value: string | null) => value ?? '—',
            },
          ]}
        />
      </Space>
    </List>
  )
}
